import React, { useMemo } from 'react';
import moment, { Moment } from 'moment';

interface CalendarPanelProps {
  selectedDate: Moment;
  onDateChange: (next: Moment) => void;
  startTime: string;
  endTime: string;
  onTimeChange: (key: 'start' | 'end', value: string) => void;
}

const CalendarPanel: React.FC<CalendarPanelProps> = ({
  selectedDate,
  onDateChange,
  startTime,
  endTime,
  onTimeChange,
}) => {
  const months = moment.months();
  const years = useMemo(() => Array.from({ length: 21 }, (_, i) => 2020 + i), []);

  const days = useMemo(() => {
    const startOfMonth = selectedDate.clone().startOf('month');
    const daysInMonth = selectedDate.daysInMonth();
    const offset = startOfMonth.day(); // 0 (Sun) - 6 (Sat)
    const placeholders = Array.from({ length: offset }, () => null);
    const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    return [...placeholders, ...monthDays];
  }, [selectedDate]);

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const monthIndex = parseInt(e.target.value, 10);
    const next = selectedDate.clone().month(monthIndex);
    const safeDay = Math.min(next.daysInMonth(), selectedDate.date());
    onDateChange(next.date(safeDay));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const yearValue = parseInt(e.target.value, 10);
    const next = selectedDate.clone().year(yearValue);
    const safeDay = Math.min(next.daysInMonth(), selectedDate.date());
    onDateChange(next.date(safeDay));
  };

  const handleSelectDay = (day?: number | null) => {
    if (!day) return;
    onDateChange(selectedDate.clone().date(day));
  };

  const goPrevMonth = () => {
    const next = selectedDate.clone().subtract(1, 'month');
    const safeDay = Math.min(next.daysInMonth(), selectedDate.date());
    onDateChange(next.date(safeDay));
  };

  const goNextMonth = () => {
    const next = selectedDate.clone().add(1, 'month');
    const safeDay = Math.min(next.daysInMonth(), selectedDate.date());
    onDateChange(next.date(safeDay));
  };

  return (
    <div className="compliance-player__calendar">
      <div className="compliance-player__calendar-controls">
        <button type="button" onClick={goPrevMonth} aria-label="Previous month">
          ‹
        </button>
        <select value={selectedDate.month()} onChange={handleMonthChange}>
          {months.map((m, idx) => (
            <option key={m} value={idx}>
              {m}
            </option>
          ))}
        </select>
        <select value={selectedDate.year()} onChange={handleYearChange}>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <button type="button" onClick={goNextMonth} aria-label="Next month">
          ›
        </button>
      </div>
      <div className="compliance-player__calendar-body">
        <div className="compliance-player__calendar-grid">
          {days.map((day, idx) => {
            const isPlaceholder = day === null;
            const isSelected = day === selectedDate.date();
            const title = isPlaceholder
              ? undefined
              : selectedDate.clone().date(day as number).format('ddd, DD.MM.YYYY');
            return (
              <span
                key={`day-${idx}`}
                className={`${isPlaceholder ? 'is-placeholder' : ''}${isSelected ? ' is-selected' : ''}`}
                title={title}
                onClick={() => handleSelectDay(day)}
              >
                {day ?? ''}
              </span>
            );
          })}
        </div>
        <div className="compliance-player__time-filters">
          <label>
            Start
            <input
              type="time"
              value={startTime}
              onChange={(e) => onTimeChange('start', e.target.value)}
            />
          </label>
          <label>
            End
            <input
              type="time"
              value={endTime}
              onChange={(e) => onTimeChange('end', e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default CalendarPanel;
